import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tab,
  Tabs,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  CreditCard,
  VpnKey,
  Notifications,
  Add,
  Delete,
} from '@mui/icons-material';
import { tokenUsageApi } from '../../services/api';

interface TokenUsageSummary {
  provider: string;
  model_name: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost: number;
  request_count: number;
  unique_users: number;
  own_key_requests: number;
  prepaid_requests: number;
}

interface TokenPricing {
  provider: string;
  model: string;
  inputPricePer1k: number;
  outputPricePer1k: number;
  markupPercentage: number;
  effectiveInputPrice: number;
  effectiveOutputPrice: number;
}

interface UserApiKey {
  id: string;
  provider: string;
  key_name: string;
  key_hint: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

interface PrepaidBalance {
  [provider: string]: number;
}

interface NotificationSettings {
  threshold: number;
  email: string | null;
  enabled: boolean;
}

const TokenUsageDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [usageSummary, setUsageSummary] = useState<TokenUsageSummary[]>([]);
  const [pricing, setPricing] = useState<TokenPricing[]>([]);
  const [apiKeys, setApiKeys] = useState<UserApiKey[]>([]);
  const [prepaidBalances, setPrepaidBalances] = useState<PrepaidBalance>({});
  const [notifications, setNotifications] = useState<NotificationSettings>({
    threshold: 100,
    email: null,
    enabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [apiKeyDialog, setApiKeyDialog] = useState(false);
  const [prepaidDialog, setPrepaidDialog] = useState(false);
  const [notificationDialog, setNotificationDialog] = useState(false);

  // Form states
  const [newApiKey, setNewApiKey] = useState({
    provider: '',
    keyName: '',
    apiKey: '',
    isDefault: false,
  });
  const [prepaidAmount, setPrepaidAmount] = useState({ provider: '', amount: 10 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, pricingRes, apiKeysRes, balancesRes, notificationsRes] = await Promise.all([
        tokenUsageApi.getUsageSummary(),
        tokenUsageApi.getPricing(),
        tokenUsageApi.getApiKeys(),
        tokenUsageApi.getPrepaidBalance(),
        tokenUsageApi.getNotificationSettings(),
      ]);

      setUsageSummary(summaryRes.data.data);
      setPricing(pricingRes.data.data);
      setApiKeys(apiKeysRes.data.data);
      setPrepaidBalances(balancesRes.data.data);
      setNotifications(notificationsRes.data.data);
    } catch (err) {
      setError('Failed to load token usage data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveApiKey = async () => {
    try {
      await tokenUsageApi.saveApiKey(newApiKey);
      setApiKeyDialog(false);
      setNewApiKey({ provider: '', keyName: '', apiKey: '', isDefault: false });
      loadData();
    } catch (err) {
      setError('Failed to save API key');
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    try {
      await tokenUsageApi.deleteApiKey(id);
      loadData();
    } catch (err) {
      setError('Failed to delete API key');
    }
  };

  const handlePurchasePrepaid = async () => {
    try {
      await tokenUsageApi.purchasePrepaid(prepaidAmount.provider, prepaidAmount.amount);
      setPrepaidDialog(false);
      setPrepaidAmount({ provider: '', amount: 10 });
      loadData();
    } catch (err) {
      setError('Failed to purchase prepaid tokens');
    }
  };

  const handleUpdateNotifications = async () => {
    try {
      await tokenUsageApi.updateNotificationSettings({
        threshold: notifications.threshold,
        email: notifications.email || undefined,
        enabled: notifications.enabled
      });
      setNotificationDialog(false);
      loadData();
    } catch (err) {
      setError('Failed to update notification settings');
    }
  };

  const getTotalCost = () => {
    return usageSummary.reduce((total, item) => total + item.total_cost, 0);
  };

  const getTotalTokens = () => {
    return usageSummary.reduce(
      (total, item) => total + item.total_input_tokens + item.total_output_tokens,
      0
    );
  };

  const getProviderColor = (provider: string): string => {
    const colors: { [key: string]: string } = {
      anthropic: '#FF6B35',
      openai: '#00A67E',
      deepseek: '#4C9AFF',
      gemini: '#4285F4',
      llama: '#FF7043',
    };
    return colors[provider] || '#666';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <div style={{ padding: 24 }}>
      <Typography variant="h4" gutterBottom>
        Token Usage Dashboard
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography color="textSecondary" variant="body2">
                    Total Spent
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(getTotalCost())}
                  </Typography>
                </div>
                <TrendingUp color="primary" />
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography color="textSecondary" variant="body2">
                    Total Tokens
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(getTotalTokens())}
                  </Typography>
                </div>
                <TrendingDown color="secondary" />
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography color="textSecondary" variant="body2">
                    Prepaid Balance
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(Object.values(prepaidBalances).reduce((a, b) => a + b, 0))}
                  </Typography>
                </div>
                <CreditCard color="success" />
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography color="textSecondary" variant="body2">
                    API Keys
                  </Typography>
                  <Typography variant="h5">
                    {apiKeys.length}
                  </Typography>
                </div>
                <VpnKey color="warning" />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper style={{ marginBottom: 24 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Usage Summary" />
          <Tab label="Pricing" />
          <Tab label="API Keys" />
          <Tab label="Prepaid Tokens" />
        </Tabs>
      </Paper>

      {/* Usage Summary Tab */}
      {tabValue === 0 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Input Tokens</TableCell>
                  <TableCell align="right">Output Tokens</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Requests</TableCell>
                  <TableCell align="right">Own Key</TableCell>
                  <TableCell align="right">Prepaid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usageSummary.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip
                        label={item.provider}
                        size="small"
                        style={{ backgroundColor: getProviderColor(item.provider), color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>{item.model_name}</TableCell>
                    <TableCell align="right">{formatNumber(item.total_input_tokens)}</TableCell>
                    <TableCell align="right">{formatNumber(item.total_output_tokens)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.total_cost)}</TableCell>
                    <TableCell align="right">{formatNumber(item.request_count)}</TableCell>
                    <TableCell align="right">{formatNumber(item.own_key_requests)}</TableCell>
                    <TableCell align="right">{formatNumber(item.prepaid_requests)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pricing Tab */}
      {tabValue === 1 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Input (per 1K)</TableCell>
                  <TableCell align="right">Output (per 1K)</TableCell>
                  <TableCell align="right">Markup</TableCell>
                  <TableCell align="right">Effective Input</TableCell>
                  <TableCell align="right">Effective Output</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricing.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip
                        label={item.provider}
                        size="small"
                        style={{ backgroundColor: getProviderColor(item.provider), color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>{item.model}</TableCell>
                    <TableCell align="right">{formatCurrency(item.inputPricePer1k)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.outputPricePer1k)}</TableCell>
                    <TableCell align="right">{item.markupPercentage}%</TableCell>
                    <TableCell align="right">{formatCurrency(item.effectiveInputPrice)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.effectiveOutputPrice)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* API Keys Tab */}
      {tabValue === 2 && (
        <Paper style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Typography variant="h6">API Keys</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setApiKeyDialog(true)}
            >
              Add API Key
            </Button>
          </div>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Key Hint</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <Chip
                        label={key.provider}
                        size="small"
                        style={{ backgroundColor: getProviderColor(key.provider), color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>{key.key_name}</TableCell>
                    <TableCell>{key.key_hint}</TableCell>
                    <TableCell>{key.is_default ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <Chip
                        label={key.is_active ? 'Active' : 'Inactive'}
                        color={key.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(key.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteApiKey(key.id)}
                      >
                        <Delete />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Prepaid Tokens Tab */}
      {tabValue === 3 && (
        <Paper style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Typography variant="h6">Prepaid Token Balances</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setPrepaidDialog(true)}
            >
              Purchase Tokens
            </Button>
          </div>
          <Grid container spacing={2}>
            {Object.entries(prepaidBalances).map(([provider, balance]) => (
              <Grid item xs={12} md={6} key={provider}>
                <Card>
                  <CardContent>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <Chip
                          label={provider}
                          style={{ backgroundColor: getProviderColor(provider), color: 'white' }}
                        />
                        <Typography variant="h6" style={{ marginTop: 8 }}>
                          {formatCurrency(balance)}
                        </Typography>
                      </div>
                      <CreditCard color="primary" />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Notification Settings Button */}
      <div style={{ position: 'fixed', bottom: 20, right: 20 }}>
        <Button
          variant="contained"
          startIcon={<Notifications />}
          onClick={() => setNotificationDialog(true)}
        >
          Notifications
        </Button>
      </div>

      {/* API Key Dialog */}
      <Dialog open={apiKeyDialog} onClose={() => setApiKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add API Key</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={newApiKey.provider}
                onChange={(e) => setNewApiKey({ ...newApiKey, provider: e.target.value })}
              >
                <MenuItem value="anthropic">Anthropic</MenuItem>
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="deepseek">DeepSeek</MenuItem>
                <MenuItem value="gemini">Google Gemini</MenuItem>
                <MenuItem value="llama">Llama</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Key Name"
              value={newApiKey.keyName}
              onChange={(e) => setNewApiKey({ ...newApiKey, keyName: e.target.value })}
              fullWidth
            />
            <TextField
              label="API Key"
              type="password"
              value={newApiKey.apiKey}
              onChange={(e) => setNewApiKey({ ...newApiKey, apiKey: e.target.value })}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveApiKey} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prepaid Purchase Dialog */}
      <Dialog open={prepaidDialog} onClose={() => setPrepaidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Purchase Prepaid Tokens</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={prepaidAmount.provider}
                onChange={(e) => setPrepaidAmount({ ...prepaidAmount, provider: e.target.value })}
              >
                <MenuItem value="anthropic">Anthropic</MenuItem>
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="deepseek">DeepSeek</MenuItem>
                <MenuItem value="gemini">Google Gemini</MenuItem>
                <MenuItem value="llama">Llama</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Amount ($)"
              type="number"
              value={prepaidAmount.amount}
              onChange={(e) => setPrepaidAmount({ ...prepaidAmount, amount: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 10 }}
            />
            <Typography variant="body2" color="text.secondary">
              You'll receive {formatCurrency(prepaidAmount.amount * 1.1)} in token credits (10% bonus)
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrepaidDialog(false)}>Cancel</Button>
          <Button onClick={handlePurchasePrepaid} variant="contained">
            Purchase
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Settings Dialog */}
      <Dialog open={notificationDialog} onClose={() => setNotificationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notification Settings</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <TextField
              label="Threshold Amount ($)"
              type="number"
              value={notifications.threshold}
              onChange={(e) => setNotifications({ ...notifications, threshold: Number(e.target.value) })}
              fullWidth
            />
            <TextField
              label="Notification Email"
              type="email"
              value={notifications.email || ''}
              onChange={(e) => setNotifications({ ...notifications, email: e.target.value || null })}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateNotifications} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TokenUsageDashboard;